import { useCallback, useState } from "react";
import { MapPanel, type PlaybackMapFix } from "./components/MapPanel";
import { CameraGrid } from "./components/CameraGrid";
import { PlaybackPanel, type PlaybackFix } from "./components/PlaybackPanel";
import { ConfirmDialog } from "./components/ConfirmDialog";
import { DeviceForm } from "./components/DeviceForm";
import { DeviceList } from "./components/DeviceList";
import { useDevices } from "./hooks/useDevices";
import { useLiveGps } from "./hooks/useLiveGps";
import { usePageRefresh } from "./lib/page-lifecycle";
import type { Device, DeviceInput } from "./api";

export default function App({
  onOpenPlayback,
  onOpenLiveTrip,
  onOpenVideoWall,
}: {
  onOpenPlayback?: (device: Device) => void;
  onOpenLiveTrip?: (device: Device) => void;
  onOpenVideoWall?: () => void;
}) {
  const { devices, loading, error, createDevice, updateDevice, deleteDevice, refresh } = useDevices();
  usePageRefresh(useCallback(() => refresh(), [refresh]));
  const { positions, connected } = useLiveGps();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panel, setPanel] = useState<"closed" | "list" | "form">("closed");
  const [editing, setEditing] = useState<Device | null>(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Device | null>(null);
  const [playbackId, setPlaybackId] = useState<string | null>(null);
  const [playbackFix, setPlaybackFix] = useState<PlaybackMapFix | null>(null);

  const onSubmit = async (input: DeviceInput) => {
    setFormBusy(true);
    setFormError(null);
    try {
      if (editing) {
        await updateDevice(editing.id, input);
      } else {
        await createDevice(input);
      }
      setPanel("list");
      setEditing(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setFormBusy(false);
    }
  };

  const playbackDevice = playbackId ? devices.find((d) => d.id === playbackId) ?? null : null;

  const handleClosePlayback = useCallback(() => {
    setPlaybackId(null);
    setPlaybackFix(null);
  }, []);

  const handlePlaybackFix = useCallback((fix: PlaybackFix | null) => {
    if (!fix) {
      setPlaybackFix((prev) => (prev === null ? prev : null));
      return;
    }
    setPlaybackFix((prev) => {
      const next: PlaybackMapFix = {
        deviceId: fix.deviceId,
        lat: fix.position.lat,
        lon: fix.position.lon,
        heading: fix.position.heading_deg,
        speedKmh: fix.position.speed_kmh,
        recordedAt: fix.recordedAt,
        gap: fix.gap,
        trail: fix.trail,
      };
      if (
        prev &&
        prev.deviceId === next.deviceId &&
        prev.lat === next.lat &&
        prev.lon === next.lon &&
        prev.heading === next.heading &&
        prev.speedKmh === next.speedKmh &&
        prev.recordedAt === next.recordedAt &&
        prev.gap === next.gap &&
        prev.trail === next.trail
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  return (
    <div className="dashboard-page">
      <div className="dashboard-toolbar">
        <span className={`ws ${connected ? "on" : "off"}`}>{connected ? "GPS feed connected" : "GPS feed reconnecting..."}</span>
        {onOpenVideoWall ? (
          <button type="button" className="primary" onClick={onOpenVideoWall}>
            Video Wall
          </button>
        ) : null}
        <button type="button" onClick={() => setPanel(panel === "closed" ? "list" : "closed")}>
          Devices
        </button>
        <button
          type="button"
          className="primary"
          onClick={() => {
            setEditing(null);
            setFormError(null);
            setPanel("form");
          }}
        >
          Add camera
        </button>
      </div>

      {error && <div className="banner">{error}</div>}
      {loading && devices.length === 0 && <div className="banner">Loading devices…</div>}

      {playbackDevice ? (
        <>
          <PlaybackPanel
            device={playbackDevice}
            embedMap={false}
            onClose={handleClosePlayback}
            onFix={handlePlaybackFix}
            onSwitchDevice={(id) => {
              setSelectedId(id);
              setPlaybackId(id);
            }}
          />
          <MapPanel
            devices={devices}
            live={positions}
            selectedId={playbackDevice.id}
            onSelect={setSelectedId}
            gpsSocketConnected={connected}
            playbackFix={playbackFix}
          />
        </>
      ) : (
        <>
          <MapPanel
            devices={devices}
            live={positions}
            selectedId={selectedId}
            onSelect={setSelectedId}
            gpsSocketConnected={connected}
          />
          <CameraGrid
            devices={devices}
            selectedId={selectedId}
            onSelect={setSelectedId}
            gps={positions}
            gpsSocketConnected={connected}
            onToggleLiveTrip={onOpenLiveTrip ? (id) => {
              const device = devices.find((d) => d.id === id);
              if (device) onOpenLiveTrip(device);
            } : undefined}
            onPlayback={(d) => {
              if (onOpenPlayback) {
                onOpenPlayback(d);
                return;
              }
              setSelectedId(d.id);
              setPlaybackId(d.id);
            }}
          />
        </>
      )}

      {panel !== "closed" && (
        <aside className="drawer">
          <div className="drawer-head">
            <h2>Camera management</h2>
            <button type="button" className="ghost" onClick={() => setPanel("closed")}>
              Close
            </button>
          </div>
          {panel === "form" ? (
            <DeviceForm
              initial={editing}
              onSubmit={onSubmit}
              onCancel={() => setPanel("list")}
              busy={formBusy}
              error={formError}
            />
          ) : (
            <>
              <DeviceList
                devices={devices}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onEdit={(d) => {
                  setEditing(d);
                  setFormError(null);
                  setPanel("form");
                }}
                onDelete={setPendingDelete}
              />
              <button
                type="button"
                className="primary full"
                onClick={() => {
                  setEditing(null);
                  setFormError(null);
                  setPanel("form");
                }}
              >
                Add camera
              </button>
            </>
          )}
        </aside>
      )}
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        danger
        title="Delete camera?"
        message={`Delete ${pendingDelete?.label || pendingDelete?.serial_no}? This cannot be undone.`}
        confirmLabel="Delete"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) void deleteDevice(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
